import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  AttachMoney,
  Send,
  Close,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import clientService from '../services/clientService';
import ConflictDetector from '../shared/validators/conflict-detector';
import { axelionColors } from '../theme/axelionTheme';

/**
 * MaslaXat Premium Booking Modal Component
 * Elegant consultation booking dialog
 */
const BookingModal = ({ open, onClose, lawyer }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    description: '',
    preferredDate: '',
    preferredTime: '',
    consultationType: 'video',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.question.trim()) {
      toast.error('Пожалуйста, опишите ваш вопрос');
      return;
    }

    if (!formData.preferredDate || !formData.preferredTime) {
      toast.error('Пожалуйста, выберите желаемую дату и время');
      return;
    }

    try {
      setLoading(true);

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      const bookingData = {
        ...formData,
        lawyerId: lawyer.id,
        lawyerName: lawyer.name,
        client: {
          id: currentUser.id,
          name: currentUser.name,
          avatar: null,
        },
        status: 'pending',
      };

      const existingConsultations = JSON.parse(
        localStorage.getItem('consultationRequests') || '[]'
      );

      const validation = ConflictDetector.validateConsultationBooking(bookingData, {
        existingConsultations,
        minHoursAhead: 2,
        maxConsultationsPerDay: 10,
      });

      ConflictDetector.logConflicts(validation);

      if (!validation.isValid) {
        const message = ConflictDetector.formatConflictMessage(validation);
        toast.error(message || 'Обнаружены конфликты при бронировании');
        setLoading(false);
        return;
      }

      if (validation.warnings.length > 0) {
        validation.warnings.forEach((warning) => {
          toast.warning(warning.message, { autoClose: 5000 });
        });
      }

      await clientService.lawyers.bookConsultation(lawyer.id, bookingData);

      toast.success('Запрос отправлен юристу! Ожидайте подтверждения.');
      onClose();

      setFormData({
        question: '',
        description: '',
        preferredDate: '',
        preferredTime: '',
        consultationType: 'video',
      });
    } catch (error) {
      toast.error('Ошибка отправки запроса');
    } finally {
      setLoading(false);
    }
  };

  if (!lawyer) return null;

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00',
    '14:00', '15:00', '16:00', '17:00',
    '18:00', '19:00', '20:00',
  ];

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: `1px solid ${axelionColors.borderLight}`,
          boxShadow: '0 24px 48px rgba(26, 26, 26, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
          px: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: '1.25rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: axelionColors.textDark,
          }}
        >
          Consultation Request
        </Typography>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 'auto',
            color: axelionColors.textMuted,
            '&:hover': {
              color: axelionColors.textDark,
              backgroundColor: axelionColors.bgWarm,
            },
          }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: axelionColors.bgCream, py: 3, px: 3 }}>
        {/* Lawyer info card */}
        <Box
          sx={{
            bgcolor: axelionColors.bgLight,
            borderRadius: '8px',
            p: 3,
            mb: 3,
            border: `1px solid ${axelionColors.borderLight}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: axelionColors.bgBeige,
                color: axelionColors.gold,
                fontSize: '1.5rem',
                fontWeight: 300,
                border: `2px solid ${axelionColors.gold}`,
              }}
            >
              {lawyer.name.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '1.1rem',
                  color: axelionColors.textDark,
                  letterSpacing: '0.02em',
                }}
              >
                {lawyer.name}
              </Typography>
              <Typography
                sx={{
                  color: axelionColors.textSecondary,
                  fontSize: '0.875rem',
                }}
              >
                {lawyer.specializations?.join(', ') || lawyer.specialization || ''}
              </Typography>
            </Box>
            <Chip
              icon={<AttachMoney sx={{ fontSize: 14, color: axelionColors.gold }} />}
              label={`${(lawyer.priceFrom || lawyer.price || lawyer.profile?.price || 0).toLocaleString()} сум`}
              sx={{
                bgcolor: axelionColors.accentLight,
                border: `1px solid ${axelionColors.gold}`,
                fontWeight: 500,
                fontSize: '0.8rem',
                color: axelionColors.gold,
                borderRadius: '8px',
                '& .MuiChip-icon': {
                  color: axelionColors.gold,
                },
              }}
            />
          </Box>
        </Box>

        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: '8px',
            bgcolor: axelionColors.infoLight,
            border: `1px solid ${axelionColors.info}20`,
            '& .MuiAlert-icon': {
              color: axelionColors.info,
            },
          }}
        >
          Ваш запрос будет отправлен юристу. После подтверждения дата и время консультации будут зафиксированы.
        </Alert>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>
              Тип консультации
            </InputLabel>
            <Select
              value={formData.consultationType}
              onChange={(e) => handleChange('consultationType', e.target.value)}
              label="Тип консультации"
              sx={{
                bgcolor: axelionColors.bgLight,
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: axelionColors.borderLight,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: axelionColors.gold,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: axelionColors.gold,
                  borderWidth: 1,
                },
              }}
            >
              <MenuItem value="video">Видео-консультация</MenuItem>
              <MenuItem value="chat">Чат-консультация</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Опишите ваш вопрос *"
            multiline
            rows={3}
            value={formData.question}
            onChange={(e) => handleChange('question', e.target.value)}
            placeholder="Кратко опишите суть вашего вопроса..."
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: axelionColors.bgLight,
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: axelionColors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: axelionColors.gold,
                },
                '&.Mui-focused fieldset': {
                  borderColor: axelionColors.gold,
                  borderWidth: 1,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: axelionColors.gold,
              },
            }}
          />

          <TextField
            label="Дополнительная информация"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Предоставьте дополнительные детали..."
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: axelionColors.bgLight,
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: axelionColors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: axelionColors.gold,
                },
                '&.Mui-focused fieldset': {
                  borderColor: axelionColors.gold,
                  borderWidth: 1,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: axelionColors.gold,
              },
            }}
          />

          <Divider sx={{ borderColor: axelionColors.borderLight }} />

          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.8rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: axelionColors.textSecondary,
            }}
          >
            Желаемая дата и время
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Дата *"
              type="date"
              value={formData.preferredDate}
              onChange={(e) => handleChange('preferredDate', e.target.value)}
              InputProps={{
                startAdornment: <CalendarToday sx={{ mr: 1, color: axelionColors.textMuted, fontSize: 18 }} />,
              }}
              inputProps={{
                min: minDate,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: axelionColors.bgLight,
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: axelionColors.borderLight,
                  },
                  '&:hover fieldset': {
                    borderColor: axelionColors.gold,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: axelionColors.gold,
                    borderWidth: 1,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: axelionColors.gold,
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>
                Время *
              </InputLabel>
              <Select
                value={formData.preferredTime}
                onChange={(e) => handleChange('preferredTime', e.target.value)}
                label="Время *"
                startAdornment={<AccessTime sx={{ mr: 1, color: axelionColors.textMuted, fontSize: 18 }} />}
                sx={{
                  bgcolor: axelionColors.bgLight,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: axelionColors.borderLight,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: axelionColors.gold,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: axelionColors.gold,
                    borderWidth: 1,
                  },
                }}
              >
                {timeSlots.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Alert
            severity="warning"
            sx={{
              borderRadius: '8px',
              bgcolor: axelionColors.warningLight,
              border: `1px solid ${axelionColors.warning}20`,
              '& .MuiAlert-icon': {
                color: axelionColors.warning,
              },
            }}
          >
            Дата и время будут зафиксированы только после подтверждения юриста.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: axelionColors.bgLight,
          borderTop: `1px solid ${axelionColors.borderLight}`,
          p: 3,
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '8px',
            border: `1px solid ${axelionColors.borderLight}`,
            color: axelionColors.textSecondary,
            px: 3,
            py: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 500,
            fontSize: '0.8rem',
            '&:hover': {
              borderColor: axelionColors.textSecondary,
              backgroundColor: axelionColors.bgWarm,
            },
          }}
        >
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <Send sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: '8px',
            bgcolor: axelionColors.gold,
            color: '#FFFFFF',
            px: 3,
            py: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 500,
            fontSize: '0.8rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: axelionColors.goldDark,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: axelionColors.bgBeige,
              color: axelionColors.textMuted,
            },
          }}
        >
          {loading ? 'Отправка...' : 'Отправить запрос'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingModal;
